import React, { useRef, useState } from 'react';
import PageHeader from './components/PageHeader';
import DialogAlert from './components/DialogAlert';
import DialogFooter from './components/DialogFooter';
import { useAnimatedClose } from './hooks/useAnimatedClose';
import { usePackageLocationSelection } from './hooks/usePackageLocationSelection';

export default function PackageImportDialog({ onClose, onNext, organizationId }) {
  const {
    markets,
    facilities,
    marketId: market,
    facilityId: facility,
    selectMarket,
    selectFacility,
  } = usePackageLocationSelection(organizationId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const isSubmittingRef = useRef(false);
  const { close, isClosing } = useAnimatedClose(onClose);

  const validate = () => {
    const next: Record<string, any> = {};
    if (!market) next.market = 'Market is required';
    if (!facility) next.facility = 'Facility is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
      if (isSubmittingRef.current) return;
      if (!validate()) return;

      isSubmittingRef.current = true;
      setIsSubmitting(true);
      try {
        await onNext({
            market,
            facility,
            marketName: markets.find(m => m.id == market)?.name,
            facilityName: facilities.find(f => f.id == facility)?.name,
            metrcFacilityId: facilities.find(f => f.id == facility)?.metrcFacilityId,
        });
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
  };

  const footer = (
    <DialogFooter>
      <button
        className="button"
        onClick={handleSave}
        data-testid="package-import-next"
        disabled={isSubmitting}
        aria-busy={isSubmitting}
      >
        {isSubmitting ? 'Loading…' : 'Next'}
      </button>
    </DialogFooter>
  );

  return (
    <div className={`stack page-slide-in${isClosing ? ' page-slide-out' : ''}`} data-testid="package-import">
      <PageHeader
        title="Import Packages"
        subtitle="Packages"
        actions={(
          <button className="button secondary" onClick={close} data-testid="package-import-back" disabled={isSubmitting}>Back</button>
        )}
      />

      <div className="drawer-body" style={{"gridTemplateColumns": "minmax(0, 1fr) minmax(0, 2fr)"}}>
        <div className="drawer-preview">
          <div className="card" style={{ padding: 16 }}>
            <div className="muted" style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Preview</div>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Import Setup</div>
            <div className="muted" style={{ fontSize: 13, display: 'grid', gap: 6 }}>
              <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Market:</span> <span data-testid="package-import-preview-market">{markets.find(m => m.id == market)?.name || '—'}</span></div>
              <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Facility:</span> <span data-testid="package-import-preview-facility">{facilities.find(f => f.id == facility)?.name || '—'}</span></div>
              <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Available Facilities:</span> <span data-testid="package-import-available-facilities">{facilities.length}</span></div>
            </div>
            <div className="divider" style={{ margin: '12px 0' }} />
            <div className="muted" style={{ fontSize: 12 }}>Next you’ll select which packages to import.</div>
          </div>
        </div>
        <div className="drawer-controls">
          <div className="drawer-controls-card">
            <DialogAlert show={Object.keys(errors).length > 0} testId="package-import-validation-error" />

            <div className="form-grid">
              <div className="field full-span">
                <label className="label">Market <span className="required-dot" aria-label="required">*</span></label>
                <select
                  className={`select ${errors.market ? 'error' : ''}`}
                  value={market}
                  onChange={(e) => selectMarket(e.target.value)}
                  data-testid="package-import-market"
                >
                  <option value="">Select market</option>
                  {[...markets]
                    .sort((a, b) => (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' }))
                    .map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
                {errors.market && <div className="error-text" data-testid="package-import-market-error">{errors.market}</div>}
              </div>

              <div className="field full-span">
                <label className="label">Facility <span className="required-dot" aria-label="required">*</span></label>
                <select
                  className={`select ${errors.facility ? 'error' : ''}`}
                  value={facility}
                  onChange={(e) => selectFacility(e.target.value)}
                  disabled={!market}
                  data-testid="package-import-facility"
                >
                   <option value="">Select facility</option>
                        {[...facilities]
                    .sort((a, b) => `${a.name} — ${a.license}`.localeCompare(`${b.name} — ${b.license}`, undefined, { sensitivity: 'base' }))
                    .map((f) => (
                    <option key={f.license} value={f.id}>{f.name} — {f.license}</option>
                  ))}
                </select>
                {errors.facility && <div className="error-text" data-testid="package-import-facility-error">{errors.facility}</div>}
              </div>
            </div>

            {footer}
          </div>
        </div>
      </div>
    </div>
  );
}
